// =============================================================================
// Alertas do AF-01 na tela (migrations 053, 059 e 060)
//
// Os três gatilhos que o motor grava desde a 009 e ninguém via: teto do mês
// atingido, mensalidade fixa que variou acima do limite da linha, e consumo
// acima do limite antes de estourar. Só os que ainda valem — o alerta velho,
// de uma linha cujo consumo caiu, a view já tira.
//
// 059: o alerta de mensalidade traz o impacto projetado no ano — "o Financeiro
// já recebe esse número junto com o alerta de upgrade" (AF-01 §8.1).
// 060: a tendência — categoria com 3 meses seguidos acima do orçado, com a
// faixa dos últimos seis meses e o convite a pedir revisão (AF-01 §9).
//
// Usado no Painel do Gestor (um CC, com "Justificar" e "Pedir revisão") e na
// Visão financeira (todos os CCs, só leitura).
//
// "Detalhes" (pedido do Financeiro de 20/10, item 2.1): abre os lançamentos
// que compõem o valor, pelo drilldown POR COORDENADA (CC + categoria + mês) —
// o mesmo da Planilha Orçado vs Realizado. O alerta abre no mês dele; a
// tendência, no último mês da sequência, e cada mês da faixa abre o seu.
// O que cada um vê quem recorta é a RLS: a tela não filtra por papel.
// =============================================================================

import { useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  buscarAlertas,
  buscarTendencia,
  type Alerta,
  type FaixaTendencia,
  type MesTendencia,
} from "../lib/api.ts";
import { competenciaCurta, competenciaLonga, moeda, percentual, type Tom } from "../lib/formato.ts";
import { mesAbertoHoje, tendenciasSeguidas, type TendenciaCategoria } from "../lib/tendencia.ts";
import { ModalLancamentos } from "../paginas/ModalLancamentos.tsx";
import { CabecalhoGrafico, Painel, Pilula, Vazio } from "./ui.tsx";

/** O que o drilldown abre — o mesmo tipo que o Painel do Gestor usa. */
type AlvoDrilldown = Parameters<typeof ModalLancamentos>[0]["linha"];

/** Chave de um mês de uma coordenada na resposta da tendência. */
const chaveMes = (centroCustoId: string, categoriaId: string, competencia: string) =>
  `${centroCustoId}|${categoriaId}|${competencia}`;

/**
 * O drilldown de uma coordenada num mês.
 *
 * O cabeçalho do modal é o da COORDENADA, porque a lista também é: vem da
 * tendência, que é por CC + categoria (060) e cobre os seis meses que terminam
 * no fim do período. Fora dela, vale o que houver — no alerta, o orçado e o
 * realizado da linha (053), que costumam ser os mesmos quando a categoria tem
 * uma linha só no mês; sem nada, zero, como a Planilha faz com o mês sem orçado.
 */
function alvoDoMes(
  c: {
    centro_custo_id: string;
    centro_custo_codigo: string;
    centro_custo_nome: string;
    categoria_id: string;
    categoria_nome: string;
  },
  competencia: string,
  mes: MesTendencia | undefined,
  reserva?: { orcado_centavos: string; realizado_centavos: string; pct: string },
): AlvoDrilldown {
  return {
    centro_custo_id: c.centro_custo_id,
    centro_custo_codigo: c.centro_custo_codigo,
    centro_custo_nome: c.centro_custo_nome,
    categoria_id: c.categoria_id,
    categoria_nome: c.categoria_nome,
    competencia,
    orcado_centavos: mes?.orcado_centavos ?? reserva?.orcado_centavos ?? "0",
    realizado_centavos: mes?.realizado_centavos ?? reserva?.realizado_centavos ?? "0",
    pct_execucao: mes ? (mes.pct_execucao ?? "0") : (reserva?.pct ?? "0"),
  };
}

/**
 * "Detalhes" é a ação secundária do item: link discreto, menor e sem borda,
 * para não competir com "Justificar" e "Pedir revisão", que são o que o aviso
 * pede ao gestor.
 */
function BotaoDetalhes({ onClick, title }: { onClick: () => void; title: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="shrink-0 rounded-md px-1.5 py-1 text-[12px] font-medium whitespace-nowrap text-tinta-media underline decoration-linha-forte underline-offset-2 hover:text-azul hover:decoration-azul"
    >
      Detalhes
    </button>
  );
}

const GATILHO: Record<Alerta["gatilho"], { rotulo: string; tom: Tom; frase: (a: Alerta) => string }> = {
  limite_mes: {
    rotulo: "Teto atingido",
    tom: "risco",
    frase: (a) => `consumiu ${percentual(a.pct_consumido)} do orçado do mês`,
  },
  variacao_fixa: {
    rotulo: "Mensalidade variou",
    tom: "risco",
    frase: (a) => `o valor passou ${percentual(a.pct_alerta_fixo, 0)} acima do orçado (${percentual(a.pct_consumido)})`,
  },
  consumo_addon: {
    rotulo: "Perto do teto",
    tom: "atencao",
    frase: (a) => `já consumiu ${percentual(a.pct_consumido)} — o aviso é a partir de ${percentual(a.pct_alerta_variavel, 0)}`,
  },
};

const DICA =
  "Avisos do AF-01, gerados a cada sincronização: teto do mês atingido; mensalidade fixa que passou do limite de variação da linha (30% por padrão), com o efeito no ano; consumo acima do limite (80% por padrão) antes de estourar; e categoria acima do orçado em 3 meses seguidos (o mês em andamento só conta se já estourou). Só aparecem os que ainda valem. O gestor tem 1 mês para justificar o teto atingido. Justificar como uso pontual tira o aviso; os outros motivos o mantêm, porque ele é o aviso ao Financeiro. \"Detalhes\" abre os lançamentos da categoria no mês; na tendência, cada mês da faixa abre os dele.";

/** Cor da célula de cada mês na faixa da tendência — a régua do mapa de calor. */
const COR_FAIXA: Record<FaixaTendencia, { classe: string; nome: string }> = {
  estouro: { classe: "bg-risco-linha", nome: "acima do orçado" },
  atencao: { classe: "bg-atencao-linha", nome: "perto do limite" },
  ok: { classe: "bg-ok-linha", nome: "dentro do orçado" },
  sem_orcamento: { classe: "bg-neutro-linha", nome: "gasto sem orçamento" },
  sem_movimento: { classe: "border border-dashed border-linha-forte", nome: "sem movimento" },
};

/**
 * O prazo para o gestor justificar o teto atingido (063): 1 mês a partir do
 * aviso. Vencido não bloqueia nada — o alerta fica, marcado.
 */
function PrazoJustificativa({ prazo }: { prazo: string }) {
  const vencido = prazo < hojeSP();
  return (
    <p className={`num mt-0.5 text-[12px] ${vencido ? "font-semibold text-risco-tinta" : "text-tinta-media"}`}>
      {vencido ? `Prazo para justificar venceu em ${dataLonga(prazo)}` : `Justificar até ${dataLonga(prazo)}`}
    </p>
  );
}

/** "2026-11-15" → "15/11/2026", sem passar por Date: é data de calendário. */
function dataLonga(d: string): string {
  const [ano, mes, dia] = d.split("-");
  return dia && mes && ano ? `${dia}/${mes}/${ano}` : d;
}

/** Hoje em São Paulo, AAAA-MM-DD. */
function hojeSP(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/Sao_Paulo" }).format(new Date());
}

/** "No ano: +R$ 480 até dezembro (R$ 120 × 4 meses). Passa o orçado anual da categoria em R$ 180." */
function ImpactoNoAno({ a }: { a: Alerta }) {
  if (a.impacto_ano_centavos === null || a.meses_restantes === null) return null;
  const excede = BigInt(a.excede_ano_centavos ?? "0");
  const meses = a.meses_restantes === 1 ? "1 mês" : `${a.meses_restantes} meses`;
  return (
    <p className="num mt-0.5 text-[12.5px] text-tinta-media">
      No ano: +{moeda(a.impacto_ano_centavos)} até dezembro ({moeda(a.aumento_mensal_centavos)} × {meses}).{" "}
      {excede > 0n ? (
        <span className="font-semibold text-risco-tinta">
          Passa o orçado anual da categoria em {moeda(a.excede_ano_centavos)}.
        </span>
      ) : (
        <span>Cabe na folga do ano da categoria ({moeda(a.folga_ano_centavos)}).</span>
      )}
    </p>
  );
}

/**
 * Os seis meses da categoria, do mais antigo ao mais novo, na régua de cores
 * do mapa de calor. Cada mês é um botão que abre os lançamentos dele: é a troca
 * de mês da tendência, feita aqui e não dentro do modal — a faixa já é a lista
 * dos meses que a tendência mostra, e o modal continua um só, o de sempre.
 */
function FaixaSeisMeses({ t, onAbrir }: { t: TendenciaCategoria; onAbrir: (competencia: string) => void }) {
  return (
    <div className="flex items-end gap-0.5" role="group"
      aria-label={`Últimos seis meses: ${t.faixas.map((f) => `${competenciaCurta(f.competencia)} ${COR_FAIXA[f.faixa].nome}`).join(", ")}. Escolha um mês para ver os lançamentos.`}>
      {t.faixas.map((f) => {
        const descricao = `${competenciaLonga(f.competencia)}: ${COR_FAIXA[f.faixa].nome}${f.pct ? ` (${percentual(f.pct)})` : ""}`;
        return (
          <button
            key={f.competencia}
            type="button"
            onClick={() => onAbrir(f.competencia)}
            title={`${descricao} — ver lançamentos`}
            aria-label={`${descricao}. Ver lançamentos.`}
            className="group flex flex-col items-center gap-0.5 rounded-[4px] p-0.5 hover:bg-superficie-2"
          >
            <span className={`block h-3 w-6 rounded-[4px] group-hover:ring-1 group-hover:ring-azul ${COR_FAIXA[f.faixa].classe}`} />
            <span className="text-[10px] leading-none text-tinta-fraca group-hover:text-azul">
              {competenciaCurta(f.competencia).slice(0, 3)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export function BlocoAlertas({
  periodo,
  centroCustoId,
  mostrarCentro = false,
  onJustificar,
  onPedirRevisao,
  className = "mt-6",
}: {
  periodo: string[];
  centroCustoId?: string;
  /** Na Visão financeira os alertas são de todos os CCs: mostra de qual. */
  mostrarCentro?: boolean;
  /** Painel do Gestor: abre a justificativa da linha. Só teto e variação. */
  onJustificar?: (a: Alerta) => void;
  /** Painel do Gestor: abre o pedido de revisão do orçamento (tendência). */
  onPedirRevisao?: () => void;
  className?: string;
}) {
  const q = useQuery({
    queryKey: ["alertas", periodo, centroCustoId ?? "todos"],
    queryFn: () => buscarAlertas(periodo, centroCustoId),
  });
  const qt = useQuery({
    queryKey: ["tendencia", periodo, centroCustoId ?? "todos"],
    queryFn: () => buscarTendencia(periodo, centroCustoId),
  });
  const alertas = q.data?.alertas ?? [];
  // A tendência é um extra: se a rota falhar, os alertas do mês continuam.
  const tendencias = qt.data
    ? tendenciasSeguidas(qt.data.meses, qt.data.referencia, { mesAberto: mesAbertoHoje() })
    : [];
  const total = alertas.length + tendencias.length;

  // Os números de cada mês de cada coordenada, para o cabeçalho do drilldown.
  const mesesDaTendencia = new Map<string, MesTendencia>();
  for (const m of qt.data?.meses ?? []) {
    mesesDaTendencia.set(chaveMes(m.centro_custo_id, m.categoria_id, m.competencia), m);
  }

  // Um drilldown por vez. `fechar` estável: o Modal refaz o efeito (foco e
  // rolagem do fundo) a cada `onFechar` novo.
  const [detalhando, setDetalhando] = useState<AlvoDrilldown | null>(null);
  const fechar = useCallback(() => setDetalhando(null), []);

  const abrirTendencia = (t: TendenciaCategoria, competencia: string) =>
    setDetalhando(alvoDoMes(t, competencia,
      mesesDaTendencia.get(chaveMes(t.centro_custo_id, t.categoria_id, competencia))));

  const abrirAlerta = (a: Alerta) =>
    setDetalhando(alvoDoMes(a, a.competencia,
      mesesDaTendencia.get(chaveMes(a.centro_custo_id, a.categoria_id, a.competencia)),
      { orcado_centavos: a.orcado_centavos, realizado_centavos: a.realizado_centavos, pct: a.pct_consumido }));

  return (
    <Painel className={className}>
      <CabecalhoGrafico titulo={total > 0 ? `Alertas (${total})` : "Alertas"} dica={DICA} />
      {q.isPending ? (
        <p className="text-[13px] text-tinta-fraca">Carregando…</p>
      ) : q.isError ? (
        <Vazio titulo="Não foi possível carregar os alertas" dica="Tente recarregar a página." />
      ) : total === 0 ? (
        <Vazio
          titulo="Nenhum alerta no período"
          dica="Nenhuma linha atingiu o teto, passou do limite de variação ou chegou perto de estourar, e nenhuma categoria está acima do orçado há 3 meses seguidos."
        />
      ) : (
        <ul className="thin-scroll max-h-[420px] overflow-y-auto pr-1">
          {tendencias.map((t) => (
            <li key={`t-${t.chave}`} className="flex flex-wrap items-center justify-between gap-3 border-t border-linha py-2.5 first:border-t-0">
              <div className="min-w-[240px] flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Pilula tom="risco">Tendência</Pilula>
                  <span className="text-[13px] font-semibold">
                    {mostrarCentro && <span className="text-tinta-media">{t.centro_custo_codigo} · </span>}
                    {t.categoria_nome}
                  </span>
                </div>
                <p className="mt-0.5 text-[12.5px] text-tinta-media">
                  Acima do orçado há {t.seguidos} meses seguidos
                  {t.ate !== qt.data?.referencia && `, até ${competenciaLonga(t.ate)}`} — se o gasto mudou de
                  patamar, vale pedir a revisão do orçamento
                </p>
              </div>
              <FaixaSeisMeses t={t} onAbrir={(c) => abrirTendencia(t, c)} />
              <div className="flex shrink-0 items-center gap-1">
                {/* Abre no último mês da sequência; os outros, pela faixa. */}
                <BotaoDetalhes
                  onClick={() => abrirTendencia(t, t.ate)}
                  title={`Lançamentos de ${competenciaLonga(t.ate)}. Para outro mês, clique nele na faixa.`}
                />
                {onPedirRevisao && (
                  <button
                    type="button"
                    onClick={onPedirRevisao}
                    className="shrink-0 rounded-lg border border-linha px-3 py-1.5 text-[12.5px] font-semibold whitespace-nowrap hover:border-laranja hover:text-laranja-tinta"
                  >
                    Pedir revisão
                  </button>
                )}
              </div>
            </li>
          ))}
          {alertas.map((a) => {
            const g = GATILHO[a.gatilho];
            return (
              <li key={a.alerta_id} className="flex flex-wrap items-start justify-between gap-3 border-t border-linha py-2.5 first:border-t-0">
                <div className="min-w-[240px] flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Pilula tom={g.tom}>{g.rotulo}</Pilula>
                    <span className="text-[13px] font-semibold">
                      {mostrarCentro && <span className="text-tinta-media">{a.centro_custo_codigo} · </span>}
                      {a.categoria_nome}
                    </span>
                    <span className="text-[12px] text-tinta-fraca">{competenciaLonga(a.competencia)}</span>
                  </div>
                  <p className="num mt-0.5 text-[12.5px] text-tinta-media">{g.frase(a)}</p>
                  {a.gatilho === "variacao_fixa" && <ImpactoNoAno a={a} />}
                  {a.prazo_justificativa && <PrazoJustificativa prazo={a.prazo_justificativa} />}
                  {a.justificativa && (
                    <p className="mt-1 rounded-lg bg-atencao-bg px-2 py-1 text-[12px] text-atencao-tinta">
                      Gestor justificou: {a.justificativa}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <BotaoDetalhes
                    onClick={() => abrirAlerta(a)}
                    title={`Lançamentos de ${a.categoria_nome} em ${competenciaLonga(a.competencia)}`}
                  />
                  {onJustificar && a.gatilho !== "consumo_addon" && (
                    <button
                      type="button"
                      onClick={() => onJustificar(a)}
                      className="shrink-0 rounded-lg border border-linha px-3 py-1.5 text-[12.5px] font-semibold whitespace-nowrap hover:border-laranja hover:text-laranja-tinta"
                    >
                      {a.justificativa ? "Justificar de novo" : "Justificar"}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {detalhando && <ModalLancamentos linha={detalhando} onFechar={fechar} />}
    </Painel>
  );
}
